/**
 * Mission-theater glossary: short plain-language entries for UI terms.
 * Pure data + helpers (no DOM).
 */

export type GlossaryEntry = {
  /** Display term (title case as shown). */
  term: string;
  /** Stable id for anchors / tests. */
  id: string;
  /** Optional short category label. */
  category: "mission" | "vehicle" | "physics" | "views";
  /** One or two sentence definition. */
  definition: string;
};

/**
 * Alphabetical glossary for tothemoon.
 * Keep definitions theater-grade honest (not flight-ops claims).
 */
export const GLOSSARY: readonly GlossaryEntry[] = [
  {
    id: "ascent",
    term: "Ascent",
    category: "mission",
    definition:
      "Powered climb from liftoff through staging into the early orbital arc. In this theater the stack follows a staged Super Heavy + Ship profile with throttle and hot-stage cues.",
  },
  {
    id: "auto-cam",
    term: "Auto-cam",
    category: "views",
    definition:
      "Guided camera that matches the Flight 13 webcast (left pane when split) and eases to a sensible view when a lunar phase changes. Toggle with G. Picking or moving a Free camera turns it off. Fixed cameras cannot be moved.",
  },
  {
    id: "ballistic-coast",
    term: "Ballistic coast",
    category: "physics",
    definition:
      "Unpowered free-flight under gravity only (no continuous burns). After translunar injection the Starbase → Moon pack is pure ballistic n-body until impact or flyby.",
  },
  {
    id: "boostback",
    term: "Boostback burn",
    category: "vehicle",
    definition:
      "Booster engines re-light after stage separation to reverse downrange velocity toward the landing zone (chopsticks or offshore). Super Heavy recovery is integrated on the Earth force model. Chopsticks seats on the tower; Flight 13 gulf is a hard splash after a 10→8→5 landing-burn relight.",
  },
  {
    id: "booster-cam",
    term: "Booster",
    category: "views",
    definition:
      "Free exterior camera looking at Super Heavy. After stage-out it follows the detached booster. Distinct from the Grid fin hull-cam mounted on the booster.",
  },
  {
    id: "chopsticks",
    term: "Chopsticks / Mechazilla",
    category: "vehicle",
    definition:
      "The tower arms at Starbase that catch Super Heavy on return. Mechazilla is the tower; the chopsticks are the pair of beams that close around the booster. Theater silhouette only — not a mechanical sim.",
  },
  {
    id: "cross-section",
    term: "Cross-section",
    category: "views",
    definition:
      "Black-and-white true-scale diagram of the launch plane: Earth surface, atmosphere shell, and booster path from liftoff through chopsticks. Open with the Cross-section button or Tab cycle.",
  },
  {
    id: "delta-v",
    term: "Δv (delta-v)",
    category: "physics",
    definition:
      "Change in velocity from thrust (km/s in this app). Propellant use follows the rocket equation; the complete card reports translunar injection Δv when available.",
  },
  {
    id: "dogleg",
    term: "Dogleg",
    category: "mission",
    definition:
      "Paid plane-change burn while in low Earth orbit to align with the lunar transfer plane. Appears as a timeline event without a separate phase id.",
  },
  {
    id: "earth-gc",
    term: "Earth GC",
    category: "views",
    definition:
      "Whole-Earth great-circle cross-section for the Flight 13 corridor (Starbase · Gauteng · Indian Ocean landing · Australia). Best-fit plane through those sites; true scale.",
  },
  {
    id: "ecliptic",
    term: "Ecliptic plane",
    category: "physics",
    definition:
      "Earth’s orbital plane around the Sun. The theater frame is ecliptic J2000 (XY = ecliptic, +Z = ecliptic north). The Polar map looks along +Z onto that plane.",
  },
  {
    id: "fixed-cam",
    term: "Fixed camera",
    category: "views",
    definition:
      "Livestream-style mounts on the Fixed rail (launch drone, Ground Camera One, Tower One / Tower Two peak cams, Starbase, trench, engine bay, hull, grid-fin, ship fin, recovery drone). Pan, orbit, and zoom are locked; a notice appears if you try. Pick a Free camera to look around.",
  },
  {
    id: "flight-graphs",
    term: "Flight graphs",
    category: "views",
    definition:
      "Dashboard of altitude, ground speed, and non-gravitational acceleration for Starship and Super Heavy across the flight. A vertical line marks the theater clock. Open with Tab. The mission pauses while it is open and resumes on close if it was playing.",
  },
  {
    id: "flame-trench",
    term: "Flame trench",
    category: "vehicle",
    definition:
      "Open channel under the launch mount that directs engine exhaust away from the pad at liftoff. The Flame trench camera on the Fixed rail stands there looking up at the Super Heavy engine bells.",
  },
  {
    id: "free-cam",
    term: "Free camera",
    category: "views",
    definition:
      "Sun, Earth, Moon, Launch Tower, Booster, and Starship. Pan, orbit, and zoom are allowed. On Sun / Earth / Moon, WASD slides in the ecliptic plane. Choosing one turns Auto-cam off.",
  },
  {
    id: "ground1",
    term: "Ground Camera One",
    category: "views",
    definition:
      "Fixed pad camera matching the Flight 13 T−2:00 webcast still: telephoto up at the full stack and Mechazilla from south-southwest of the OLM. Movement is locked.",
  },
  {
    id: "hardstand",
    term: "Hardstand",
    category: "vehicle",
    definition:
      "The concrete apron around the launch mount — roads, slabs, and tank farm at true pad scale. Distinct from the tan coastal brush (scrub) outside the concrete.",
  },
  {
    id: "hot-stage",
    term: "Hot-staging",
    category: "vehicle",
    definition:
      "Ship engines light while still attached, then Super Heavy separates. In the theater this is a short dual-plume window before free-flyer recovery of the booster.",
  },
  {
    id: "kepler-corridor",
    term: "Kepler corridor",
    category: "physics",
    definition:
      "Dashed amber 2-body reference ellipse from the translunar injection state, overlaid on the n-body trail (toggle O with orbits). Metrics show max |Δr| between the two paths.",
  },
  {
    id: "tower",
    term: "Launch Tower",
    category: "views",
    definition:
      "Free camera looking at Mechazilla. WASD slides parallel to the Earth; T/B is straight up and down from the pad.",
  },
  {
    id: "aerial",
    term: "Launchpad Drone",
    category: "views",
    definition:
      "Fixed flying drone over the Starbase pad. It flies the Flight 13 webcast path: the T− wide tableau of Mechazilla, the stack, and the coastline, then a perch above the pad for liftoff where the lens tilts up as the stack climbs past. Distinct from the sea-level recovery Drone after splash. Movement is locked.",
  },
  {
    id: "leo",
    term: "Low Earth orbit (LEO)",
    category: "mission",
    definition:
      "Near-circular parking orbit after ascent/circularize, before dogleg and translunar injection. Altitude is hundreds of km above Earth, not yet a lunar transfer.",
  },
  {
    id: "max-q",
    term: "Max Q",
    category: "mission",
    definition:
      "Moment of peak aerodynamic dynamic pressure on ascent. Flight-test timelines mark it; the theater models a throttle dip near this band on Super Heavy.",
  },
  {
    id: "n-body",
    term: "N-body / restricted n-body",
    category: "physics",
    definition:
      "Craft integrated under Earth, Moon, and Sun point-mass gravity (plus Earth J₂ and simple drag at low altitude). Bodies follow prescribed ephemerides; the craft does not back-react on them.",
  },
  {
    id: "olm",
    term: "OLM (Orbital Launch Mount)",
    category: "vehicle",
    definition:
      "Steel ring-table the Super Heavy stack sits on at Starbase. Open in the center so engine exhaust dumps into the flame trench; the Flame trench camera looks through that opening at the Raptors.",
  },
  {
    id: "payload",
    term: "Payload bay",
    category: "views",
    definition:
      "Fixed Starship camera beside the Pez door, looking out at the Starlink V3 stack as it deploys — the Flight 13 T+16:46 through T+21:19 webcast beat. Movement is locked.",
  },
  {
    id: "polar-map",
    term: "Polar map",
    category: "views",
    definition:
      "Earth-centric 2-D map of ship and Moon paths looking along ecliptic +Z (perpendicular to Earth’s orbital plane). Solid ship trail, dashed Moon path, osculating lunar-orbit ring through the Moon.",
  },
  {
    id: "raptor",
    term: "Raptor",
    category: "vehicle",
    definition:
      "SpaceX methane/oxygen engine family on Super Heavy and Starship. Flight 13 demos include multi-engine landing burns and an in-space single-engine relight on Ship.",
  },
  {
    id: "scrubber",
    term: "Scrubber",
    category: "views",
    definition:
      "Mission-time slider on the transport bar. Phase and event marks are ticks on the track; hover a tick to read its clock, name, and detail on the info bar between the news ticker and the slider, or click to jump. The LIVE news ticker above that bar follows the same mission clock.",
  },
  {
    id: "seco",
    term: "SECO (ship engine cutoff)",
    category: "mission",
    definition:
      "Starship main engines cut off after the upper-stage burn (ascent or suborbital coast insertion on flight tests). Marks the start of coast or payload demos on Flight 13’s timeline.",
  },
  {
    id: "starbase",
    term: "Starbase",
    category: "mission",
    definition:
      "SpaceX launch site at Boca Chica, Texas. Both missions start from the pad theater (OLM, flame trench, tower, chopsticks) at true geographic scale on the spinning Earth.",
  },
  {
    id: "starship",
    term: "Starship / Super Heavy",
    category: "vehicle",
    definition:
      "Two-stage stack: Super Heavy booster + Starship upper stage (Ship). The mesh is near-true size; after stage-out the booster free-flies while Ship continues the mission.",
  },
  {
    id: "tower1cam",
    term: "Tower One Cam",
    category: "views",
    definition:
      "Fixed camera on the peak of OLP-1 Mechazilla, looking west at the live OLP-2 stack — an elevated full-stack framing of the pad hold. Movement is locked.",
  },
  {
    id: "tower2cam",
    term: "Tower Two Cam",
    category: "views",
    definition:
      "Fixed camera on the peak of the live OLP-2 launch tower, looking down at the stack from the vehicle-facing edge of the deck. Flight 13 Auto-cam pans it with the vehicle from T+3 to T+8 — the webcast tower-down cut. Movement is locked.",
  },
  {
    id: "tli",
    term: "Translunar injection (TLI)",
    category: "mission",
    definition:
      "Finite burn that raises apogee toward the Moon. After TLI the baked Moon mission coasts ballistically; outcome is lunar impact or flyby in the current pack.",
  },
  {
    id: "true-scale",
    term: "True scale",
    category: "physics",
    definition:
      "Scene unit is 1 km: Earth/Moon radii and cislunar distances match real orders of magnitude. Craft length is tens of meters, so system views use STARSHIP and SUPER HEAVY name plates when the mesh is tiny.",
  },
] as const;

const CATEGORY_ORDER: readonly GlossaryEntry["category"][] = [
  "mission",
  "vehicle",
  "physics",
  "views",
];

const CATEGORY_LABEL: Record<GlossaryEntry["category"], string> = {
  mission: "Mission phases & events",
  vehicle: "Vehicle",
  physics: "Physics & scale",
  views: "Views & controls",
};

/** Category display label. */
export function glossaryCategoryLabel(
  cat: GlossaryEntry["category"],
): string {
  return CATEGORY_LABEL[cat];
}

/** Entries sorted by category group, then alphabetically by term. */
export function glossaryGrouped(): {
  category: GlossaryEntry["category"];
  label: string;
  entries: GlossaryEntry[];
}[] {
  return CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_LABEL[category],
    entries: GLOSSARY.filter((e) => e.category === category).sort((a, b) =>
      a.term.localeCompare(b.term),
    ),
  })).filter((g) => g.entries.length > 0);
}

/** Look up by stable id. */
export function glossaryById(id: string): GlossaryEntry | undefined {
  return GLOSSARY.find((e) => e.id === id);
}
